import React, { useContext, useEffect, useState } from 'react'
import { FiCalendar, FiCheck, FiEye, FiPlay, FiTrash } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { Formik, Form } from 'formik'
import { format, startOfDay } from 'date-fns'

import Button from '~/components/Form/Button'
import Card from '~/components/Card'
import Lista from './Lista'
import Cadastro from './Cadastro'
import { Container, ModalInicioFimAtividade } from './styles'
import Modal from '~/components/Modal'
import Pesquisa from './Pesquisar'
import Table from '~/components/Table'
import { openTabSafari } from '~/util/file'
import formUtils from '~/util/form'
import { api, authHeaders } from '~/services/api'
import { GlobalContext } from '~/contexts/GlobalContext'
import { calcularDuracao } from '~/util/date'
import { File, Textbox } from '~/components/Form'
import useQuery from '~/hooks/useQuery'
import masks from '~/util/masks'
import Yup from '~/services/yup'


function normalizar(item) {
    return {
        id: item.id,
        pessoa: item.pessoa,
        pessoa_nome: item.pessoa.nome,
        instituicao_nome: item.instituicao?.nome_fantasia || item.instituicao_descricao,
        motivo: item.motivo,
        assunto: item.assunto.descricao,
        data_previsao: new Date(item.data_prevista),
        data_previsao_string: format(new Date(item.data_prevista), 'dd/MM/yyyy'),
        data_visita: item.inicio ? startOfDay(new Date(item.inicio)) : null,
        data_visita_string: item.inicio ? format(new Date(item.inicio), 'dd/MM/yyyy') : '',
        inicio: item.inicio ? new Date(item.inicio) : null,
        fim: item.fim ? new Date(item.fim) : null,
        periodo: item.inicio && item.fim ? `${format(new Date(item.inicio), 'HH:mm\'h\'')} às ${format(new Date(item.fim), 'HH:mm\'h\'')}` : '',
        duracao: item.duracao !== null ? calcularDuracao(item.duracao) : '',
        validado: item.validado,
        validador: item.validador,
        validador_nome: item.validador?.nome || '-',
        validado_string: item.validado ? 'Sim' : 'Não',
        data_validacao: item.validacao_data_hora,
        data_validacao_string: item.validacao_data_hora ? format(new Date(item.validacao_data_hora), 'dd/MM/yyyy') : null,
        background: !item.inicio ? '#bdd8ff' : item.inicio && !item.fim ? '#ffcdd2' : item.inicio && item.fim && !item.validado ? '#fff59d' : undefined
    }
}

export default function () {
    const { user } = useContext(GlobalContext)

    const { id } = useQuery()

    const [mostrarCadastro, setMostrarCadastro] = useState(false)
    const [visitas, setVisitas] = useState(null)
    const [assuntos, setAssuntos] = useState(null)
    const [visitaAberta, setVisitaAberta] = useState(null)
    const [validadoresIds, setValidadoresIds] = useState(null)
    const [documentos, setDocumentos] = useState([])
    const [mostrarConfirmacaoInicio, setMostrarConfirmacaoInicio] = useState(false)
    const [mostrarConfirmacaoFim, setMostrarConfirmacaoFim] = useState(false)
    const [mostrarConfirmacaoValidacao, setMostrarConfirmacaoValidacao] = useState(false)

    async function loadVisitas() {
        setVisitas(null)

        let response = await api.get('visita_diretoria', authHeaders())

        response = response.map(normalizar)

        setVisitas(response)
    }

    async function loadValidadores() {
        const response = await api.get('visita_diretoria/validador', authHeaders())

        setValidadoresIds(response.map(item => item.pessoa.id))
    }

    async function loadAssuntos() {
        const response = await api.get('visita_diretoria/assunto', authHeaders())

        setAssuntos(response.map(a => ({
            label: a.descricao,
            value: a.id
        })))
    }

    async function reloadVisitaAberta() {
        try {
            if (visitaAberta) {
                const response = await api.get(`visita_diretoria/${visitaAberta.id}`, authHeaders())

                setVisitaAberta(normalizar(response))
            }
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handlePesquisar(values) {
        setVisitas(null)

        values = formUtils.extractFormValues(values)

        let response = await api.post('visita_diretoria/pesquisar', values, authHeaders())

        response = response.map(normalizar)

        setVisitas(response)
    }

    async function handleCadastrar(values) {
        values = formUtils.extractFormValues(values)

        await api.post('visita_diretoria', values, authHeaders())

        loadVisitas()

        setMostrarCadastro(false)

        toast.success('A atividade foi agendada.')
    }

    function handleAbrirDocumento(doc) {
        openTabSafari(doc.link)
    }

    async function handleValidar() {
        try {
            await api.patch(`visita_diretoria/${visitaAberta.id}/validar`, {}, authHeaders())

            toast.success('Atividade validada com sucesso.')

            reloadVisitaAberta()
            loadVisitas()

            setMostrarConfirmacaoValidacao(false)
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function loadDocumentosVisita(visita) {
        try {
            const response = await api.get(`visita_diretoria/${visita.id}`, authHeaders())

            setDocumentos(response.documentos.map(doc => ({
                id: doc.arquivo.id,
                descricao: doc.descricao,
                link: doc.arquivo.link
            })))
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleIniciarVisita(values) {
        try {
            await api.patch(`visita_diretoria/${visitaAberta.id}/iniciar`, {
                inicio: values.inicio
            }, authHeaders())

            toast.success('Atividade iniciada.')

            reloadVisitaAberta()
            loadVisitas()

            setMostrarConfirmacaoInicio(false)
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleConcluirVisita(values) {
        try {
            await api.patch(`visita_diretoria/${visitaAberta.id}/concluir`, {
                fim: values.fim
            }, authHeaders())

            toast.success('Atividade concluída.')

            reloadVisitaAberta()
            loadVisitas()

            setMostrarConfirmacaoFim(false)
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleExcluirVisita(visita) {
        try {
            await api.delete(`visita_diretoria/${visita.id}`, authHeaders())

            toast.success('Atividade excluída.')

            loadVisitas()
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleEnviarDocumento(values, { resetForm }) {
        try {
            await api.post(`visita_diretoria/${visitaAberta.id}/documento`, values, authHeaders())

            toast.success('Documento enviado.')

            reloadVisitaAberta()
            loadVisitas()

            resetForm()
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleRemoverDocumento(doc) {
        try {
            await api.delete(`visita_diretoria/${visitaAberta.id}/documento/${doc.id}/remover`, authHeaders())

            toast.success('Documento removido.')

            reloadVisitaAberta()
            loadVisitas()
        } catch (e) {
            toast.error(e.msg)
        }
    }

    useEffect(() => {
        loadVisitas()

        loadAssuntos()

        loadValidadores()
    }, [])

    useEffect(() => {
        if (visitaAberta) {
            loadDocumentosVisita(visitaAberta)
        }
    }, [visitaAberta])

    useEffect(() => {
        if (visitas && id) {
            setVisitaAberta(visitas.find(v => v.id === +id) || null)
        }
    }, [visitas, id])

    const eDonoDaVisita = visitaAberta?.pessoa.id === user?.pessoa.id
    const podeIniciarVisita = eDonoDaVisita && !visitaAberta?.inicio
    const podeTerminarVisita = eDonoDaVisita && visitaAberta?.inicio && !visitaAberta?.fim
    const podeValidar = validadoresIds?.includes(user?.pessoa.id) && !visitaAberta?.validado && !!visitaAberta?.duracao && !eDonoDaVisita
    const podeGerenciarDocumentosDaVisita = eDonoDaVisita && !visitaAberta?.validado

    return (
        <>
            <Container>
                <main>
                    <Card>
                        <div className="flex flex-col gap-4">
                            <h1>Atividades da diretoria</h1>

                            <Button className="white self-end" onClick={() => { setMostrarCadastro(true) }}>
                                Agendar atividade
                                <FiCalendar size={17} />
                            </Button>

                            <Pesquisa
                                handlePesquisar={handlePesquisar}
                                validadoresIds={validadoresIds}
                            />

                            <Lista
                                visitas={visitas}
                                handleOpenVisita={setVisitaAberta}
                                handleExcluirVisita={handleExcluirVisita}
                                validadoresIds={validadoresIds}
                            />
                        </div>
                    </Card>
                </main>
            </Container>

            <Modal
                isOpen={mostrarCadastro}
                handleClose={() => {
                    setMostrarCadastro(false)
                }}
                title="Agendar atividade"
            >
                <Cadastro
                    handleCadastrar={handleCadastrar}
                    assuntos={assuntos}
                />
            </Modal>

            <Modal
                isOpen={!!visitaAberta}
                handleClose={() => {
                    setVisitaAberta(null)
                }}
                title="Detalhes da atividade"
            >
                {visitaAberta && (
                    <div className="flex flex-col md:grid md:grid-cols-[3fr_1fr]">
                        <div className="mx-2 my-4 space-y-2">
                            <p>
                                <span>Diretor: </span>
                                <span className="font-bold">{visitaAberta.pessoa_nome}</span>
                            </p>

                            <p>
                                <span>Local: </span>
                                <span className="font-bold">{visitaAberta.instituicao_nome}</span>
                            </p>

                            <p>
                                <span>Assunto: </span>
                                <span className="font-bold">{visitaAberta.assunto}</span>
                            </p>

                            <p>
                                <span>Motivo: </span>
                                <span className="font-bold">{visitaAberta.motivo}</span>
                            </p>

                            <p>
                                <span>Data prevista: </span>
                                <span className="font-bold">{visitaAberta.data_previsao_string}</span>
                            </p>

                            <p>
                                <span>Data da atividade: </span>
                                {visitaAberta.data_visita ? (
                                    <span className="font-bold">
                                        {visitaAberta.data_visita_string}
                                        {!!visitaAberta.duracao ? ` - ${visitaAberta.periodo} (~${visitaAberta.duracao})` : ' (em curso)'}
                                        {visitaAberta.inicio && !visitaAberta.fim ? ` iniciada às ${format(new Date(visitaAberta.inicio), 'HH:mm\'h\'')}` : null}
                                    </span>
                                ) : (
                                    <span className="font-bold"> Não realizada</span>
                                )}
                            </p>

                            <p>
                                <span>Validação: </span>
                                <span className="font-bold">{!visitaAberta.validado && 'Não'}</span>

                                {visitaAberta.validado && (
                                    <span className="font-bold">{`Validado por ${visitaAberta.validador_nome} em ${visitaAberta.data_validacao_string}`}</span>
                                )}
                            </p>
                        </div>

                        {podeValidar ? (
                            <Button
                                className="white justify-self-end w-fit"
                                onClick={() => setMostrarConfirmacaoValidacao(true)}
                            >
                                Validar atividade
                                <FiCheck size={16} />
                            </Button>
                        ) : podeIniciarVisita ? (
                            <Button
                                className="white justify-self-end w-fit"
                                onClick={() => { setMostrarConfirmacaoInicio(true) }}
                            >
                                Iniciar atividade
                                <FiPlay size={16} />
                            </Button>
                        ) : podeTerminarVisita ? (
                            <Button
                                className="white justify-self-end w-fit"
                                onClick={() => { setMostrarConfirmacaoFim(true) }}
                            >
                                Concluir atividade
                                <FiCheck size={16} />
                            </Button>
                        ) : null}

                        <h2 className="font-semibold col-start-1 mx-2" style={{ fontSize: 16 }}>Documentos relacionados</h2>

                        <div className="flex flex-col lg:grid lg:grid-cols-2 col-span-2 gap-6">
                            {podeGerenciarDocumentosDaVisita && (
                                <Formik
                                    initialValues={{
                                        descricao: '',
                                        arquivo: null
                                    }}
                                    validationSchema={Yup.object(
                                        {
                                            descricao: Yup.string().required('Informe o nome do documento.'),
                                            arquivo: Yup.number().nullable().required('Anexe o documento.')
                                        }
                                    )}
                                    onSubmit={handleEnviarDocumento}
                                    enableReinitialize
                                >
                                    {({ errors, setFieldValue }) => (
                                        <Form className="flex flex-col">
                                            <Textbox
                                                name="descricao"
                                                label="Identificação do documento"
                                            />

                                            <File
                                                name="arquivo"
                                                onSuccess={fileId => setFieldValue('arquivo', fileId)}
                                                label="Documento"
                                                format="square"
                                                error={errors.arquivo}
                                                style={{ width: 'calc(100% - 16px)' }}
                                                previewSize={['100%', '140px']}
                                            />

                                            <Button type="submit" className="transparent self-end">
                                                Enviar documento
                                            </Button>
                                        </Form>
                                    )}
                                </Formik>
                            )}

                            <Table
                                headers={[
                                    { name: 'descricao', value: 'Documento' }
                                ]}
                                data={documentos}
                                actions={[
                                    {
                                        action: handleAbrirDocumento,
                                        icon: FiEye,
                                        iconSize: 16,
                                        name: 'Visualizar'
                                    },
                                    {
                                        action: handleRemoverDocumento,
                                        name: 'Remover',
                                        icon: FiTrash,
                                        iconSize: 16,
                                        confirmation: 'Deseja realmente remover este documento?',
                                        checkDisabled: () => !podeGerenciarDocumentosDaVisita
                                    }
                                ]}
                                hideOffset={400}
                            />
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                isOpen={mostrarConfirmacaoInicio}
                handleClose={() => {
                    setMostrarConfirmacaoInicio(false)
                }}
                title="Iniciar atividade"
                style={{ width: 'fit-content' }}
            >
                <ModalInicioFimAtividade>
                    <>
                        <p className="mx-2">Informe a data e hora de ínicio da atividade.</p>

                        <div className="flex flex-col gap-3">
                            <Formik
                                initialValues={
                                    { inicio: '' }
                                }
                                onSubmit={handleIniciarVisita}
                                validationSchema={Yup.object().shape({
                                    inicio: Yup.string().nullable().required('Informe data e hora de ínicio da atividade.')
                                })}
                            >
                                {() => (
                                    <Form>
                                        <Textbox
                                            name="inicio"
                                            label="Data e hora"
                                            mask={masks.datetime}
                                        />

                                        <Button type="submit" className="white self-center">
                                            Iniciar
                                        </Button>
                                    </Form>
                                )}

                            </Formik>
                        </div>
                    </>
                </ModalInicioFimAtividade>
            </Modal>

            <Modal
                isOpen={mostrarConfirmacaoFim}
                handleClose={() => {
                    setMostrarConfirmacaoFim(false)
                }}
                title="Finalizar atividade"
            >
                <ModalInicioFimAtividade>
                    <>
                        <p className="mx-2">Informe a data e hora do fim da atividade.</p>

                        <div className="flex flex-col gap-3">
                            <Formik
                                initialValues={
                                    { fim: '' }
                                }
                                onSubmit={handleConcluirVisita}
                                validationSchema={Yup.object().shape({
                                    fim: Yup.string().nullable().required('Informe data e hora do fim da atividade.')
                                })}
                            >
                                {() => (
                                    <Form>
                                        <Textbox
                                            name="fim"
                                            label="Data e hora"
                                            mask={masks.datetime}
                                        />

                                        <Button type="submit" className="white self-center">
                                            Concluir
                                        </Button>
                                    </Form>
                                )}

                            </Formik>
                        </div>
                    </>
                </ModalInicioFimAtividade>
            </Modal>

            <Modal
                isOpen={mostrarConfirmacaoValidacao}
                handleClose={() => {
                    setMostrarConfirmacaoValidacao(false)
                }}
                title="Confirmação"
            >
                <div className="flex flex-col gap-3">
                    <p className="mx-2">Deseja realmente validar esta atividade?</p>

                    <div className="flex self-end">
                        <Button onClick={() => setMostrarConfirmacaoValidacao(false)} className="transparent">
                            Não
                        </Button>

                        <Button onClick={handleValidar} className="white">
                            Sim
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}
